import React, { useEffect, useState } from 'react';
import {
  FiActivity,
  FiArchive,
  FiCheckSquare,
  FiInfo,
  FiKey,
  FiPieChart,
  FiTrendingUp,
  FiUsers,
} from 'react-icons/fi';
import {
  PALETTE,
  Card,
  CardLabel,
  CardRow,
  CardValue,
  ErrText,
  Hint,
  SectionTitle,
  smallBtnStyle,
} from '../station/stationTheme';
import {
  DistributionCard,
  PermissionsCard,
  VotingCard,
  WithdrawCard,
} from './CorpCards';
import type { CorpDetail } from './types';

interface Props {
  detail: CorpDetail;
  busy: boolean;
  error: string | null;
  onWithdraw: (args: { corpId: string; amount: number }) => void;
  onSetDistribution: (args: { corpId: string; percentage: number; periodDays: number }) => void;
  onStartVote: (args: { corpId: string; nomineeName: string }) => void;
  onCastVote: (args: { voteId: string; decision: 'remove' | 'keep' }) => void;
  onSetPermission: (args: { corpId: string; targetUsername: string; flags: number }) => void;
  onKickMember: (args: { corpId: string; targetName: string }) => void;
  onRespondApplication: (args: { corpId: string; applicantName: string; decision: 'accept' | 'reject' }) => void;
}

type SectionId =
  | 'overview'
  | 'bank'
  | 'owners'
  | 'members'
  | 'distributions'
  | 'vote'
  | 'permissions'
  | 'audit';

/**
 * Sub-page layout inspired by StationLobby: a vertical menu of corp facets on
 * the left, the selected facet's content on the right. Replaces the previous
 * "everything stacked in one scroll" approach so the drawer doesn't feel like
 * a dumping ground. Sections are filtered by what's actually available in the
 * server-side DTO (e.g. permissions and audit only surface for the manager).
 */
export function CorpDetailView({
  detail: d,
  busy,
  error,
  onWithdraw,
  onSetDistribution,
  onStartVote,
  onCastVote,
  onSetPermission,
  onKickMember,
  onRespondApplication,
}: Props) {
  const hasBank = d.bankBalance !== null && d.bankBalance !== undefined;
  const hasOwners = (d.owners?.length ?? 0) > 0;
  const hasDistribution = !!d.distribution;
  const hasVote = !!d.activeVote || hasOwners;
  const canManagePerms = Array.isArray(d.permissionRows);
  const hasAudit = Array.isArray(d.auditLog);
  const pendingApps = Array.isArray(d.pendingApplications) ? d.pendingApplications.length : 0;

  const sections: Array<{ id: SectionId; label: string; icon: React.ReactNode; badge?: number; available: boolean }> = [
    { id: 'overview', label: 'Overview', icon: <FiInfo size={14} />, available: true },
    { id: 'bank', label: 'Bank', icon: <FiArchive size={14} />, available: hasBank },
    { id: 'owners', label: 'Owners', icon: <FiPieChart size={14} />, available: hasOwners },
    { id: 'members', label: 'Members', icon: <FiUsers size={14} />, badge: pendingApps || undefined, available: true },
    { id: 'distributions', label: 'Distributions', icon: <FiTrendingUp size={14} />, available: hasDistribution },
    { id: 'vote', label: 'Vote', icon: <FiCheckSquare size={14} />, available: hasVote },
    { id: 'permissions', label: 'Permissions', icon: <FiKey size={14} />, available: canManagePerms },
    { id: 'audit', label: 'Audit', icon: <FiActivity size={14} />, available: hasAudit },
  ];
  const available = sections.filter((s) => s.available);

  const [active, setActive] = useState<SectionId>('overview');

  // Reset to a valid section when the corp changes or when the currently
  // active one disappears (e.g. permissions hidden after role change).
  useEffect(() => {
    if (!available.some((s) => s.id === active)) {
      setActive(available[0]?.id ?? 'overview');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d.id, available.length]);

  return (
    <div style={layoutStyle}>
      <nav style={sidebarStyle}>
        {available.map((s) => (
          <button
            key={s.id}
            style={navItemStyle(active === s.id)}
            onClick={() => setActive(s.id)}
          >
            <span style={navIconStyle(active === s.id)}>{s.icon}</span>
            <span style={navLabelStyle}>{s.label}</span>
            {s.badge && s.badge > 0 ? <span style={navBadgeStyle}>{s.badge}</span> : null}
          </button>
        ))}
      </nav>

      <div style={contentStyle}>
        {active === 'overview' && <OverviewSection detail={d} />}

        {active === 'bank' && hasBank && (
          <WithdrawCard corpId={d.id} busy={busy} onWithdraw={onWithdraw} />
        )}

        {active === 'owners' && <OwnersSection detail={d} />}

        {active === 'members' && (
          <MembersSection
            detail={d}
            busy={busy}
            onKickMember={onKickMember}
            onRespondApplication={onRespondApplication}
          />
        )}

        {active === 'distributions' && d.distribution && (
          <DistributionCard corpId={d.id} cfg={d.distribution} busy={busy} onSave={onSetDistribution} />
        )}

        {active === 'vote' && (
          <VotingCard detail={d} busy={busy} onStart={onStartVote} onCast={onCastVote} />
        )}

        {active === 'permissions' && canManagePerms && (
          <PermissionsCard
            corpId={d.id}
            detail={d}
            busy={busy}
            onSetPermission={onSetPermission}
          />
        )}

        {active === 'audit' && hasAudit && <AuditSection log={d.auditLog ?? []} />}

        {error && <ErrText>{error}</ErrText>}
      </div>
    </div>
  );
}

// ===== Sub-page sections =====

function OverviewSection({ detail: d }: { detail: CorpDetail }) {
  return (
    <Card>
      <CardRow>
        <CardLabel>Role</CardLabel>
        <CardValue>{d.isManager ? 'Manager (super-user)' : 'Member'}</CardValue>
      </CardRow>
      {d.bankBalance !== null && d.bankBalance !== undefined && (
        <CardRow>
          <CardLabel>Bank balance</CardLabel>
          <CardValue>{Number(d.bankBalance).toLocaleString()} CR</CardValue>
        </CardRow>
      )}
      <CardRow>
        <CardLabel>Owners</CardLabel>
        <CardValue>{(d.owners ?? []).length}</CardValue>
      </CardRow>
      <CardRow>
        <CardLabel>Members</CardLabel>
        <CardValue>{(d.members ?? []).length}</CardValue>
      </CardRow>
      {d.managerPlayerId === null && (
        <Hint style={{ color: PALETTE.ORANGE, marginTop: 4 }}>
          This corporation has no manager. Owners may vote to install one.
        </Hint>
      )}
    </Card>
  );
}

function OwnersSection({ detail: d }: { detail: CorpDetail }) {
  return (
    <Card>
      <SectionTitle>Owners</SectionTitle>
      {(d.owners ?? []).length === 0 && <Hint>No owners.</Hint>}
      {(d.owners ?? []).map((o) => (
        <CardRow key={o.playerId}>
          <CardLabel>
            {o.playerName}
            {o.playerId === d.managerPlayerId ? ' (manager)' : ''}
          </CardLabel>
          <CardValue>{o.percentage}%</CardValue>
        </CardRow>
      ))}
    </Card>
  );
}

function MembersSection({
  detail: d,
  busy,
  onKickMember,
  onRespondApplication,
}: {
  detail: CorpDetail;
  busy: boolean;
  onKickMember: (args: { corpId: string; targetName: string }) => void;
  onRespondApplication: (args: { corpId: string; applicantName: string; decision: 'accept' | 'reject' }) => void;
}) {
  const canKick = Array.isArray(d.pendingApplications);
  const pending = Array.isArray(d.pendingApplications) ? d.pendingApplications : [];

  return (
    <>
      <Card>
        <SectionTitle>Members ({(d.members ?? []).length})</SectionTitle>
        {(d.members ?? []).length === 0 && <Hint>No members yet.</Hint>}
        {(d.members ?? []).map((m) => (
          <CardRow key={m.playerId}>
            <CardLabel>{m.playerName}</CardLabel>
            <CardValue>
              {canKick && (
                <button
                  style={smallBtnStyle(busy)}
                  disabled={busy}
                  onClick={() => onKickMember({ corpId: d.id, targetName: m.playerName })}
                >
                  Kick
                </button>
              )}
            </CardValue>
          </CardRow>
        ))}
      </Card>

      {pending.length > 0 && (
        <Card>
          <SectionTitle>Pending applications</SectionTitle>
          {pending.map((a) => (
            <CardRow key={a.playerId}>
              <CardLabel>{a.playerName}</CardLabel>
              <CardValue>
                <span style={{ display: 'inline-flex', gap: 6 }}>
                  <button
                    style={smallBtnStyle(busy)}
                    disabled={busy}
                    onClick={() => onRespondApplication({ corpId: d.id, applicantName: a.playerName, decision: 'accept' })}
                  >
                    Accept
                  </button>
                  <button
                    style={smallBtnStyle(true)}
                    disabled={busy}
                    onClick={() => onRespondApplication({ corpId: d.id, applicantName: a.playerName, decision: 'reject' })}
                  >
                    Reject
                  </button>
                </span>
              </CardValue>
            </CardRow>
          ))}
        </Card>
      )}
    </>
  );
}

function AuditSection({ log }: { log: NonNullable<CorpDetail['auditLog']> }) {
  return (
    <Card>
      <SectionTitle>Audit log</SectionTitle>
      {log.length === 0 && <Hint>No activity yet.</Hint>}
      {log.map((a) => (
        <div
          key={a.id}
          style={{ fontSize: 10, color: PALETTE.DIM, padding: '2px 0', borderBottom: `1px solid ${PALETTE.BORDER}` }}
        >
          <span style={{ color: PALETTE.LIGHT }}>{a.action}</span>
          {a.actorName ? ` · ${a.actorName}` : ' · system'}
          {` · ${new Date(a.createdAt).toLocaleString()}`}
        </div>
      ))}
    </Card>
  );
}

// ===== STYLES =====

const layoutStyle: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  display: 'grid',
  gridTemplateColumns: '150px 1fr',
  overflow: 'hidden',
};

const sidebarStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  padding: 10,
  borderRight: `1px solid ${PALETTE.BORDER}`,
  overflowY: 'auto',
  background: `${PALETTE.DARK}66`,
};

const navItemStyle = (isActive: boolean): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 10px',
  borderRadius: 4,
  border: 'none',
  background: isActive ? `${PALETTE.TEAL}22` : 'transparent',
  cursor: 'pointer',
  textAlign: 'left',
  fontFamily: "'JetBrains Mono', monospace",
  position: 'relative',
});

const navIconStyle = (isActive: boolean): React.CSSProperties => ({
  display: 'inline-flex',
  color: isActive ? PALETTE.TEAL : PALETTE.DIM,
});

const navLabelStyle: React.CSSProperties = {
  flex: 1,
  fontSize: 11,
  color: PALETTE.LIGHT,
  letterSpacing: '0.05em',
};

const navBadgeStyle: React.CSSProperties = {
  minWidth: 16,
  height: 16,
  padding: '0 5px',
  borderRadius: 8,
  background: PALETTE.ORANGE,
  color: PALETTE.DARK,
  fontSize: 9,
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const contentStyle: React.CSSProperties = {
  overflowY: 'auto',
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  minWidth: 0,
};
